import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import './index.css';
import { validateEnvOrThrow } from './utils/validateEnv';
import './utils/extensionRejectionNoise';
import './sentry';
import './utils/setupLogging';

// Validate environment variables before app initialization
try {
  validateEnvOrThrow();
} catch (error) {
  // If validation fails, show error in console and prevent app from loading
  console.error(error);
  // Render error UI instead of app
  const root = document.getElementById('root');
  if (root) {
    root.innerHTML = `
      <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #111827; color: #f3f4f6; font-family: system-ui, -apple-system, sans-serif; padding: 2rem;">
        <div style="max-width: 600px; background: #1f2937; border: 1px solid #dc2626; border-radius: 0.5rem; padding: 2rem;">
          <h1 style="color: #ef4444; font-size: 1.5rem; font-weight: bold; margin-bottom: 1rem;">Configuration Error</h1>
          <p style="color: #d1d5db; margin-bottom: 1.5rem; white-space: pre-wrap;">${error instanceof Error ? error.message : String(error)}</p>
          <div style="background: #111827; border-radius: 0.375rem; padding: 1rem; margin-top: 1rem;">
            <p style="color: #9ca3af; font-size: 0.875rem; margin-bottom: 0.5rem;">To fix this:</p>
            <ol style="color: #d1d5db; font-size: 0.875rem; margin-left: 1.5rem; line-height: 1.75;">
              <li>Create a <code style="background: #374151; padding: 0.125rem 0.375rem; border-radius: 0.25rem;">.env</code> file in the project root</li>
              <li>Add all required environment variables</li>
              <li>Restart the development server</li>
            </ol>
          </div>
        </div>
      </div>
    `;
  }
  throw error; // Re-throw to prevent app from rendering
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false, // Don't refetch when component mounts (when navigating back)
      retry: 1,
      staleTime: 30 * 60 * 1000, // Data is considered fresh for 30 minutes
      gcTime: 60 * 60 * 1000, // Keep cached data for 1 hour after last use
      // Use placeholderData to keep previous data visible while fetching (if needed)
    }
  }
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>
);
